import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MUITypography from "../../Components/MUITypography";
import Loader from "../../Components/Loader";
import { align_center } from "../../Styles/VMStyles";
import StorageFactory from "../../Util/StorageFactory";
import { clearAuthSession } from "../../Util/authSessionHelper";
import {
  ROUTE_LOGIN,
  ROUTE_MAIN_HOME,
  SESSION_USER_COUNTRY_DATA_KEY,
  SESSION_USER_ID_KEY,
  SESSION_USER_TYPE_KEY,
  SESSION_ACCOUNT_DELETION_DATA_KEY,
} from "../../Util/VMConstants";
import { displayFormattedDateTime, isAdminLoggedIn } from "../../Util/UtilHelper";
import {
  ACCOUNT_DELETION_POLL_INTERVAL_MS,
  ACCOUNT_DELETION_STATUS,
  clearAccountDeletionSession,
  getAccountDeleteRequestStatus,
  isPendingDeletionStatus,
  revokeAccountDeleteRequest,
  setAccountDeletionSession,
  submitAccountDeleteRequest,
} from "../../Services/accountDeletionService";

const STATUS_BULLETS = [
  "Your account will be deleted within 2 days, pending admin approval.",
  "Please stay on this page or check back for updates on the status of your request.",
  "Once your account is deleted, all your data will be permanently removed and cannot be recovered.",
  "If you wish to use our services again in the future, you will need to sign up with a new account.",
  "Please ensure you've downloaded or saved any important information before proceeding.",
];

const DeleteAccountStatusScreen = ({
  deletionData,
  currencySymbol,
  revoking,
  onRevoke,
}) => {
  const isApproved =
    deletionData?.status === ACCOUNT_DELETION_STATUS.DELETE_APPROVED;
  const bannerMessage =
    deletionData?.message || "Your account deletion request is submitted.";

  return (
    <>
      <Box sx={align_center}>
        <Card
          sx={{
            backgroundColor: "#d32f2f",
            color: "#fff",
            mb: 3,
            textAlign: "center",
            width: "100%",
          }}
        >
          <CardContent>{bannerMessage}</CardContent>
        </Card>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 600, mb: 2 }}>
          {isApproved
            ? "Your account deletion request has been approved."
            : "Your account deletion request has been received!"}
        </Typography>

        {isApproved && deletionData?.scheduled_deletion_at && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your account is scheduled for deletion on{" "}
            {displayFormattedDateTime(
              deletionData.scheduled_deletion_at,
              currencySymbol,
              { wallClock: true }
            )}
            .
          </Alert>
        )}

        {!isApproved && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              You can log in again while your request is pending and you will be
              returned to this status screen. Revoke is available until admin
              approval.
            </Alert>

            <ul style={{ paddingLeft: "20px", margin: 0 }}>
              {STATUS_BULLETS.map((text) => (
                <li key={text}>
                  <Typography variant="body2">{text}</Typography>
                </li>
              ))}
            </ul>

            <Typography pt={2}>
              Thank you for being with us. We hope to see you again!
            </Typography>
          </>
        )}
      </Box>

      {deletionData?.can_revoke && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="outlined"
            color="primary"
            disabled={revoking}
            onClick={onRevoke}
            sx={{
              minWidth: 150,
              color: "primary.main",
              borderColor: "primary.main",
              "&:hover": {
                color: "white",
                backgroundColor: "primary.main",
                borderColor: "primary.main",
              },
            }}
          >
            Revoke Request
          </Button>
        </Box>
      )}
    </>
  );
};

const VMDeleteAccount = () => {
  const navigate = useNavigate();
  const sessionFactory = useMemo(() => new StorageFactory("session"), []);
  const userId = sessionFactory.get(SESSION_USER_ID_KEY);
  const userType = sessionFactory.get(SESSION_USER_TYPE_KEY);
  const currencySymbol =
    sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || "₹";
  const isAdmin = isAdminLoggedIn(userType);

  const [view, setView] = useState("loading");
  const [deletionData, setDeletionData] = useState(null);
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const applyDeletionData = useCallback(
    async (data) => {
      if (!data) return;
      setDeletionData(data);

      if (data.status === ACCOUNT_DELETION_STATUS.DELETED) {
        clearAccountDeletionSession(sessionFactory);
        await clearAuthSession();
        navigate(`/${ROUTE_LOGIN}`, { replace: true });
        return;
      }

      if (isPendingDeletionStatus(data.status)) {
        setAccountDeletionSession(sessionFactory, { pending: true, data });
        setView("status");
      } else {
        clearAccountDeletionSession(sessionFactory);
        setView("form");
      }
    },
    [navigate, sessionFactory]
  );

  const fetchStatus = useCallback(async () => {
    if (!userId || !isAdmin) return null;
    try {
      const result = await getAccountDeleteRequestStatus(userId);
      if (result?.status_code === 200 && result.data) {
        await applyDeletionData(result.data);
        return result.data;
      }
      if (result?.status_code === 404) {
        clearAccountDeletionSession(sessionFactory);
        setView("form");
      }
    } catch (error) {
      console.error("Failed to fetch delete account status:", error);
      const cachedData = sessionFactory.get(SESSION_ACCOUNT_DELETION_DATA_KEY);
      if (cachedData && isPendingDeletionStatus(cachedData.status)) {
        setDeletionData(cachedData);
        setView("status");
      } else {
        setView("form");
      }
    }
    return null;
  }, [applyDeletionData, isAdmin, sessionFactory, userId]);

  useEffect(() => {
    if (!isAdmin) {
      setView("unauthorized");
      return;
    }

    const cachedData = sessionFactory.get(SESSION_ACCOUNT_DELETION_DATA_KEY);
    if (cachedData && isPendingDeletionStatus(cachedData.status)) {
      setDeletionData(cachedData);
      setView("status");
    }

    fetchStatus();
  }, [fetchStatus, isAdmin, sessionFactory]);

  useEffect(() => {
    if (view !== "status" || !userId || !isAdmin) return undefined;

    const intervalId = window.setInterval(() => {
      fetchStatus();
    }, ACCOUNT_DELETION_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [fetchStatus, isAdmin, userId, view]);

  const handleSubmit = async () => {
    setPasswordError("");

    if (!password.trim()) {
      setPasswordError("Password is required");
      return;
    }
    if (!confirmed) {
      toast.error("Please confirm that you understand this cannot be undone");
      return;
    }

    setLoading(true);
    try {
      const result = await submitAccountDeleteRequest({
        user_id: userId,
        password,
        reason,
      });

      if (result?.status_code === 200) {
        toast.success(result.message || "Your account deletion request is submitted.");
        await applyDeletionData(result.data);
        setPassword("");
        return;
      }

      if (result?.data && isPendingDeletionStatus(result.data.status)) {
        await applyDeletionData(result.data);
        return;
      }

      if (result?.message === "Invalid Password") {
        setPasswordError(result.message);
      } else {
        toast.error(result?.message || "Failed to submit delete request");
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      const apiData = error?.response?.data?.data;

      if (apiData && isPendingDeletionStatus(apiData.status)) {
        await applyDeletionData(apiData);
        return;
      }

      if (apiMessage === "Invalid Password") {
        setPasswordError(apiMessage);
      } else {
        toast.error(apiMessage || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      const result = await revokeAccountDeleteRequest(userId);
      if (result?.status_code === 200) {
        toast.success(result.message || "Account deletion request revoked successfully");
        clearAccountDeletionSession(sessionFactory);
        setDeletionData(null);
        setView("form");
        navigate(`/${ROUTE_MAIN_HOME}`, { replace: true });
        return;
      }
      toast.error(result?.message || "Failed to revoke request");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setRevoking(false);
    }
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="sm" sx={{ mt: 5 }}>
        <Alert severity="warning">
          Only account admin can manage account deletion.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <MUITypography
        align="center"
        gutterBottom
        sx={{ mt: 5, fontWeight: 700, ...align_center, marginBottom: 3 }}
      >
        Delete Account
      </MUITypography>

      {view === "loading" ? (
        <Loader loading />
      ) : view === "form" ? (
        <>
          <Box sx={align_center}>
            <Box
              component="img"
              src="/static/delete-account.svg"
              alt="Delete Account Banner"
              sx={{ width: "100%", maxHeight: 250, objectFit: "contain", mb: 3 }}
            />
          </Box>

          <Alert severity="error" sx={{ mb: 3 }}>
            Deleting your account will permanently remove all venue data,
            enquiries, invoices, and team information. This action cannot be
            undone after admin approval.
          </Alert>

          <TextField
            label="Reason for deleting your account (optional)"
            multiline
            minRows={3}
            fullWidth
            variant="outlined"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ mb: 3 }}
          />

          <TextField
            label="Current password"
            type={showPassword ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError("");
            }}
            error={Boolean(passwordError)}
            helperText={passwordError}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                color="error"
                sx={{ py: 0.5 }}
              />
            }
            label="I understand this cannot be undone"
            sx={{
              mb: 3,
              alignItems: "center",
              "& .MuiFormControlLabel-label": { lineHeight: 1.5 },
            }}
          />

          <Box sx={align_center}>
            <Button
              variant="contained"
              color="error"
              size="large"
              sx={{ maxWidth: "300px" }}
              disabled={loading}
              onClick={handleSubmit}
            >
              Delete Account
            </Button>
          </Box>
        </>
      ) : (
        <DeleteAccountStatusScreen
          deletionData={deletionData}
          currencySymbol={currencySymbol}
          revoking={revoking}
          onRevoke={handleRevoke}
        />
      )}

      <Loader loading={loading || revoking} />
    </Container>
  );
};

export default VMDeleteAccount;
